<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import {
  suggestMapping,
  duplicatedKeys,
  missingRequiredColumns,
  hasMappedColumn,
  buildMappingPayload,
} from 'dashboard/helper/dataTransferMapping';
import DataTransferAPI from 'dashboard/api/dataTransfer';
import { useDataTransfer } from 'dashboard/composables/useDataTransfer';
import { useContactLabelsStore } from 'dashboard/stores/contactLabels';
import { useCompanyLabelsStore } from 'dashboard/stores/companyLabels';
import { useDealLabelsStore } from 'dashboard/stores/dealLabels';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import DataTransferTutorial from './DataTransferTutorial.vue';

const emit = defineEmits(['done']);

const POLL_INTERVAL_MS = 2000;

const { t } = useI18n();
const { downloadExample } = useDataTransfer();
const contactLabelsStore = useContactLabelsStore();
const companyLabelsStore = useCompanyLabelsStore();
const dealLabelsStore = useDealLabelsStore();

const dialogRef = ref(null);
const fileInput = ref(null);

const step = ref('upload');
const file = ref(null);
const headers = ref([]);
const columns = ref([]);
const mapping = ref({});
const batchLabels = ref([]);
const convertedEncoding = ref(false);
const errorMessage = ref('');
const isReading = ref(false);
const isImporting = ref(false);
const result = ref(null);
const showTutorial = ref(false);
const importId = ref(null);
const progress = ref({ processed: 0, total: 0 });
let pollTimer = null;

const reset = () => {
  step.value = 'upload';
  file.value = null;
  headers.value = [];
  columns.value = [];
  mapping.value = {};
  batchLabels.value = [];
  convertedEncoding.value = false;
  errorMessage.value = '';
  isReading.value = false;
  isImporting.value = false;
  result.value = null;
  showTutorial.value = false;
  importId.value = null;
  progress.value = { processed: 0, total: 0 };
};

const stopPolling = () => {
  clearTimeout(pollTimer);
  pollTimer = null;
};

// A importação roda em segundo plano: consulta o andamento até terminar (resultado ou interrupção).
// Devolve true quando terminou.
const handleStatus = status => {
  progress.value = {
    processed: status.processed_records,
    total: status.total_records,
  };
  if (status.status === 'completed' || status.status === 'failed') {
    result.value = status.result;
    isImporting.value = false;
    step.value = 'result';
    return true;
  }
  if (step.value !== 'mapping') step.value = 'running';
  return false;
};

const pollStatus = async () => {
  try {
    const { data } = await DataTransferAPI.importStatus(importId.value);
    if (handleStatus(data.import)) return;
  } catch (error) {
    // falha de rede passageira: tenta de novo, a importação continua no servidor
  }
  pollTimer = setTimeout(pollStatus, POLL_INTERVAL_MS);
};

// Ao abrir, retoma uma importação em andamento ou mostra o resultado que o usuário ainda não viu
const resumeImport = async () => {
  try {
    const { data } = await DataTransferAPI.importStatus();
    if (!data.import) return;
    importId.value = data.import.id;
    if (!handleStatus(data.import)) {
      pollTimer = setTimeout(pollStatus, POLL_INTERVAL_MS);
    }
  } catch (error) {
    // sem importação pendente de leitura: segue no passo 1
  }
};

const open = () => {
  reset();
  dialogRef.value?.open();
  resumeImport();
  contactLabelsStore.fetchAll();
  companyLabelsStore.fetchAll();
  dealLabelsStore.fetchAll();
};

// Etiqueta(s) de lote (D18/D21/D22): sugere títulos já usados nas 3 famílias (sem duplicata), mas
// também aceita criar um título novo — os valores enviados ao backend são sempre só os títulos
// (array de strings); TagInput em modo múltiplo já mantém batchLabels como esse array.
const batchLabelMenuItems = computed(() => {
  const titles = new Set([
    ...contactLabelsStore.getLabels.map(label => label.title),
    ...companyLabelsStore.getLabels.map(label => label.title),
    ...dealLabelsStore.getLabels.map(label => label.title),
  ]);
  return [...titles].map(title => ({
    action: 'label',
    value: title,
    label: title,
    icon: 'i-lucide-tag',
  }));
});

// Fechar (X, clique fora, Cancelar ou Fechar) descarta o estado; se criou algo, a tela atualiza a lista.
// Fechar com a importação ainda rodando não a cancela: ela continua e o resultado fica salvo.
const createdSomething = () =>
  Boolean(
    result.value &&
      result.value.contacts_created +
        result.value.companies_created +
        result.value.deals_created >
        0
  );

const acknowledgeResult = () => {
  if (result.value && importId.value) {
    DataTransferAPI.acknowledgeImport(importId.value).catch(() => {});
  }
};

const handleClose = () => {
  stopPolling();
  const refresh = createdSomething();
  acknowledgeResult();
  reset();
  if (refresh) emit('done');
};

// Do resultado direto para um novo upload, sem fechar o diálogo
const startNewImport = () => {
  const refresh = createdSomething();
  acknowledgeResult();
  reset();
  if (refresh) emit('done');
};

onBeforeUnmount(stopPolling);

const requestErrorMessage = error =>
  error?.response?.data?.error || t('DATA_TRANSFER.IMPORT.GENERIC_ERROR');

const readHeader = async selectedFile => {
  isReading.value = true;
  errorMessage.value = '';
  try {
    const { data } = await DataTransferAPI.header(selectedFile);
    file.value = selectedFile;
    headers.value = data.headers;
    columns.value = data.columns;
    convertedEncoding.value = data.converted_encoding;
    mapping.value = suggestMapping(data.headers, data.columns);
    step.value = 'mapping';
  } catch (error) {
    errorMessage.value = requestErrorMessage(error);
  } finally {
    isReading.value = false;
  }
};

const onFileChange = event => {
  const selectedFile = event.target.files?.[0];
  event.target.value = '';
  if (selectedFile) readHeader(selectedFile);
};

const goBack = () => {
  step.value = 'upload';
  errorMessage.value = '';
};

const confirmImport = async () => {
  isImporting.value = true;
  errorMessage.value = '';
  try {
    const { data } = await DataTransferAPI.import(
      file.value,
      buildMappingPayload(mapping.value),
      batchLabels.value
    );
    importId.value = data.id;
    pollTimer = setTimeout(pollStatus, POLL_INTERVAL_MS);
  } catch (error) {
    errorMessage.value = requestErrorMessage(error);
    isImporting.value = false;
  }
};

const columnOptions = computed(() =>
  columns.value.map(column => ({ value: column.key, label: column.header }))
);

const headerOf = key =>
  columns.value.find(column => column.key === key)?.header;

const duplicateMessages = computed(() =>
  duplicatedKeys(mapping.value).map(key =>
    t('DATA_TRANSFER.IMPORT.DUPLICATE_FIELD', { field: headerOf(key) })
  )
);

const entityLabels = computed(() => ({
  deal: t('DATA_TRANSFER.IMPORT.ENTITY.deal'),
  contact: t('DATA_TRANSFER.IMPORT.ENTITY.contact'),
  company: t('DATA_TRANSFER.IMPORT.ENTITY.company'),
}));

const missingMessages = computed(() =>
  missingRequiredColumns(mapping.value, columns.value).map(column =>
    t('DATA_TRANSFER.IMPORT.MISSING_FIELD', {
      field: column.label,
      entity: entityLabels.value[column.entity],
    })
  )
);

const mapsDeals = computed(() =>
  Object.values(mapping.value).some(key => key?.startsWith('deal.'))
);

const canConfirm = computed(
  () =>
    hasMappedColumn(mapping.value) &&
    duplicateMessages.value.length === 0 &&
    !isImporting.value
);

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <div class="relative flex items-center gap-2">
      <h3 class="text-base font-medium leading-6 text-n-slate-12">
        {{ t('DATA_TRANSFER.IMPORT.TITLE') }}
      </h3>
      <OnClickOutside @trigger="showTutorial = false">
        <div class="relative">
          <Button
            icon="i-lucide-info"
            color="slate"
            variant="ghost"
            size="xs"
            type="button"
            :title="t('DATA_TRANSFER.IMPORT.INFO')"
            @click="showTutorial = !showTutorial"
          />
          <DataTransferTutorial
            v-if="showTutorial"
            class="absolute z-50 mt-1 ltr:left-0 rtl:right-0 top-full"
          />
        </div>
      </OnClickOutside>
    </div>

    <!-- Passo 1: upload -->
    <div v-if="step === 'upload'" class="flex flex-col gap-4">
      <div class="flex items-center gap-3">
        <span class="text-sm text-n-slate-12 whitespace-nowrap">
          {{ t('DATA_TRANSFER.IMPORT.FILE_LABEL') }}
        </span>
        <Button
          :label="
            isReading
              ? t('DATA_TRANSFER.IMPORT.READING')
              : t('DATA_TRANSFER.IMPORT.CHOOSE_FILE')
          "
          icon="i-lucide-upload"
          color="slate"
          variant="faded"
          size="sm"
          type="button"
          :is-loading="isReading"
          :disabled="isReading"
          @click="fileInput?.click()"
        />
      </div>
      <p v-if="errorMessage" class="text-sm text-n-ruby-11">
        {{ errorMessage }}
      </p>
      <div class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-1">
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('DATA_TRANSFER.IMPORT.EXAMPLE_HINT') }}
        </p>
        <Button
          :label="t('DATA_TRANSFER.TUTORIAL.DOWNLOAD_EXAMPLE')"
          icon="i-lucide-download"
          color="slate"
          variant="faded"
          size="sm"
          type="button"
          class="!w-fit"
          @click="downloadExample"
        />
      </div>
      <div class="flex justify-end">
        <Button
          :label="t('DATA_TRANSFER.IMPORT.CANCEL')"
          color="slate"
          variant="faded"
          type="button"
          @click="dialogRef?.close()"
        />
      </div>
    </div>

    <!-- Passo 2: mapeamento -->
    <div v-else-if="step === 'mapping'" class="flex flex-col gap-4">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('DATA_TRANSFER.IMPORT.MAPPING_INSTRUCTION') }}
      </p>
      <ul class="flex flex-col gap-2 max-h-[45vh] overflow-y-auto pe-1">
        <li
          v-for="header in headers"
          :key="header"
          class="grid grid-cols-2 items-center gap-3"
        >
          <span class="text-sm truncate text-n-slate-12" :title="header">
            {{ header }}
          </span>
          <ComboBox
            :model-value="mapping[header] || ''"
            :options="columnOptions"
            :placeholder="t('DATA_TRANSFER.IMPORT.SELECT_FIELD')"
            :search-placeholder="t('DATA_TRANSFER.IMPORT.SEARCH_FIELD')"
            :empty-state="t('DATA_TRANSFER.IMPORT.NO_FIELD_FOUND')"
            @update:model-value="value => (mapping[header] = value)"
          />
        </li>
      </ul>

      <p v-if="convertedEncoding" class="mb-0 text-xs text-n-amber-11">
        {{ t('DATA_TRANSFER.IMPORT.ENCODING_CONVERTED') }}
      </p>
      <p
        v-for="message in duplicateMessages"
        :key="message"
        class="mb-0 text-xs text-n-ruby-11"
      >
        {{ message }}
      </p>
      <p
        v-for="message in missingMessages"
        :key="message"
        class="mb-0 text-xs text-n-amber-11"
      >
        {{ message }}
      </p>
      <p v-if="mapsDeals" class="mb-0 text-xs text-n-slate-11">
        {{ t('DATA_TRANSFER.IMPORT.AUTOMATIONS_NOTICE') }}
      </p>
      <p v-if="errorMessage" class="mb-0 text-sm text-n-ruby-11">
        {{ errorMessage }}
      </p>

      <div class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">
          {{ t('DATA_TRANSFER.IMPORT.BATCH_LABEL.LABEL') }}
        </span>
        <div class="px-3 py-1 rounded-lg border border-n-weak bg-n-alpha-1">
          <TagInput
            v-model="batchLabels"
            type="text"
            allow-create
            show-dropdown
            tag-icon="i-lucide-tag"
            :auto-open-dropdown="false"
            :placeholder="t('DATA_TRANSFER.IMPORT.BATCH_LABEL.PLACEHOLDER')"
            :menu-items="batchLabelMenuItems"
            :disabled="isImporting"
          />
        </div>
      </div>

      <div class="flex items-center justify-between gap-3">
        <Button
          :label="t('DATA_TRANSFER.IMPORT.BACK')"
          color="slate"
          variant="faded"
          type="button"
          :disabled="isImporting"
          @click="goBack"
        />
        <Button
          :label="
            isImporting
              ? t('DATA_TRANSFER.IMPORT.IMPORTING')
              : t('DATA_TRANSFER.IMPORT.CONFIRM')
          "
          color="blue"
          type="button"
          :is-loading="isImporting"
          :disabled="!canConfirm"
          @click="confirmImport"
        />
      </div>
    </div>

    <!-- Importação em andamento (retomada ao reabrir o diálogo) -->
    <div v-else-if="step === 'running'" class="flex flex-col gap-4">
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t('DATA_TRANSFER.IMPORT.RUNNING_TITLE') }}
      </p>
      <p v-if="progress.total" class="mb-0 text-sm text-n-slate-11">
        {{
          t('DATA_TRANSFER.IMPORT.PROGRESS', {
            processed: progress.processed,
            total: progress.total,
          })
        }}
      </p>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('DATA_TRANSFER.IMPORT.RUNNING_HINT') }}
      </p>
      <div class="flex justify-end">
        <Button
          :label="t('DATA_TRANSFER.IMPORT.CLOSE')"
          color="slate"
          variant="faded"
          type="button"
          @click="dialogRef?.close()"
        />
      </div>
    </div>

    <!-- Passo 3: resultado -->
    <div v-else-if="result" class="flex flex-col gap-3">
      <div
        v-if="result.interrupted"
        class="flex flex-col gap-1 p-3 text-sm rounded-lg bg-n-alpha-1 text-n-ruby-11"
      >
        <p class="mb-0 font-medium">{{ result.failure_message }}</p>
        <p class="mb-0 text-xs">
          {{
            t('DATA_TRANSFER.RESULT.INTERRUPTED', {
              contacts: result.contacts_created,
              companies: result.companies_created,
              deals: result.deals_created,
            })
          }}
        </p>
      </div>
      <ul class="flex flex-col gap-1 text-sm text-n-slate-12">
        <li>
          {{
            t('DATA_TRANSFER.RESULT.CONTACTS_CREATED', {
              count: result.contacts_created,
            })
          }}
        </li>
        <li>
          {{
            t('DATA_TRANSFER.RESULT.COMPANIES_CREATED', {
              count: result.companies_created,
            })
          }}
        </li>
        <li>
          {{
            t('DATA_TRANSFER.RESULT.DEALS_CREATED', {
              count: result.deals_created,
            })
          }}
        </li>
      </ul>

      <div v-if="result.error_count > 0" class="flex flex-col gap-1">
        <p class="mb-0 text-sm font-medium text-n-ruby-11">
          {{ t('DATA_TRANSFER.RESULT.ERRORS', { count: result.error_count }) }}
        </p>
        <ul
          class="flex flex-col gap-1 p-2 overflow-y-auto text-xs rounded-lg max-h-40 bg-n-alpha-1 text-n-ruby-11"
        >
          <li v-for="(entry, index) in result.errors" :key="index">
            {{ entry.message }}
          </li>
        </ul>
      </div>

      <div class="flex items-center justify-between gap-3">
        <Button
          :label="t('DATA_TRANSFER.RESULT.NEW_IMPORT')"
          color="slate"
          variant="faded"
          type="button"
          @click="startNewImport"
        />
        <Button
          :label="t('DATA_TRANSFER.IMPORT.CLOSE')"
          color="blue"
          type="button"
          @click="dialogRef?.close()"
        />
      </div>
    </div>
  </Dialog>

  <input
    ref="fileInput"
    type="file"
    accept="text/csv,.csv"
    class="hidden"
    @change="onFileChange"
  />
</template>
